// @ts-nocheck
import { createMachine, assign, fromPromise } from 'xstate'
import * as z from 'zod'
import { isValidPhoneNumber } from 'libphonenumber-js'
import type { FormSettings } from '~/form-settings'
import type { Form, FormApplication, ErrorBag } from './form-types'
import * as Sentry from "@sentry/browser";

// GA tracking flag
declare global {
  interface Window {
    __viewItemSent?: boolean;
    dataLayer: any[];
  }
}


// @ts-ignore - Complex Zod recursive type inference
export type TokenizedCreditCard = z.infer<typeof tokenizedCreditCard>

function isValidPhone (phone: string | null) {
  if (phone === null) return false

  return isValidPhoneNumber(phone)
}

const tokenizedCreditCard = z.object({
  token: z.string(),
  expMonth: z.string(),
  expYear: z.string(),
  card: z.string()
})

const errorMap: z.ZodErrorMap = (error, ctx) => {
  if (error.message) return { message: error.message }

  switch (error.code) {
    case "invalid_type":
      if (error.received == 'null') {
        return { message: 'This field is required' }
      }
      else if (error.received == 'nan') {
        return { message: 'Please enter a valid number' }
      }
    case 'too_small':
      return {
        message: 'This field is required'
      }
    case 'invalid_union':
      if (error.unionErrors.flatMap(x => x.errors).some(x => x.code == 'invalid_type' && x.received == 'null')) {
        return {
          message: 'This field is required'
        }
      }
      else {
        // Unhandled invalid_union case
      }
    default:
      return {
        message: error.message || ''
      }
  }
}

// https://gist.github.com/erberg-snippets/7895723
function isValidEmail (email: string | ' ') {
  if (!email) return true;

  if (email == ' ') return true;

  var regex = /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
  return regex.test(email.trim());
}

// Israeli ID (Teudat Zehut) validation with checksum
function isValidTeudatZehut(id: string | null): boolean {
  if (!id) return true; // Allow null/empty - required validation handled separately

  // Remove any whitespace and ensure it's exactly 9 digits
  const cleaned = id.trim().replace(/\s/g, '');
  if (!/^\d{9}$/.test(cleaned)) return false;

  // Israeli ID checksum validation (Luhn-like algorithm)
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let digit = parseInt(cleaned[i]);
    // Multiply by 1 or 2 based on position
    const multiplier = (i % 2) + 1;
    let product = digit * multiplier;
    // If product > 9, sum its digits (e.g., 14 -> 1+4 = 5)
    if (product > 9) {
      product = Math.floor(product / 10) + (product % 10);
    }
    sum += product;
  }

  return sum % 10 === 0;
}

function expectsToken (gateway: string) {
  return gateway.includes('USAePay') || gateway.includes('Moneris') || gateway.toLowerCase().includes('sensepass') || gateway.includes('MyVirtualMerchant') || gateway.toLowerCase().startsWith('cardknox')
}


export const formBase = z.object({
  callback: z.string().optional(),
  success: z.string().optional(),
  signup: z.boolean(),
  dayOfMonth: z.string().nullable(),
  dayOfWeek: z.string().nullable(),
  periodMonths: z.string().nullable(),
  periodWeeks: z.string().nullable(),
  transactionFee: z.number(),
  title: z.string(),
  applications: z.array(z.object({
    amount: z.union([z.number(),z.string()]).nullable(),
    department: z.string(),
    campaign: z.string(),
    subCampaign: z.string().nullable().optional()
  })),
  accountID: z.union([z.string(), z.number()]).nullable().optional(),
  pledgeId: z.string().nullable(),
  firstName: z.union([z.string(), z.null(), z.undefined()]),
  lastName: z.union([z.string(), z.null(), z.undefined()]),
  email: z.union([z.string(), z.null(), z.undefined()])
    .refine((val) => !val || isValidEmail(val), {
      message: 'This email is not valid'
    }),
  phone: z.union([z.string(), z.null(), z.undefined()])
    .refine((val) => !val || isValidPhone(val), {
      message: 'This phone is not valid'
    }),
  taxId: z.union([z.string(), z.null(), z.undefined()])
    .refine((val) => !val || isValidTeudatZehut(val), {
      message: 'Invalid Teudat Zehut - must be 9 digits with valid checksum'
    }),
  street: z.union([z.string(), z.null(), z.undefined()]),
  street2: z.string().nullable(),
  city: z.union([z.string(), z.null(), z.undefined()]),
  state: z.union([z.string(), z.null(), z.undefined()]),
  zip: z.union([z.string(), z.null(), z.undefined()]),
  country: z.string().nullable(),
  description: z.string().nullable(),
  currency: z.union([z.literal('USD'), z.literal('CAD'), z.literal('ILS')]),
  gateway: z.union(
    [
      z.string(),
      z.literal('check'),
      z.literal('wire'),
    ]),
  amount: z.coerce.number().min(5, 'Minimum amount is $5'),
  handleProcessing: z.boolean(),
  recipientName: z.string().nullable(),
  recipientEmail: z.string().nullable(),
  honoreeName: z.string().nullable(),
  dedication: z.string().nullable(),
  dedicationMessage: z.string().nullable(),
  toc: z.boolean(),
  custom: z.object({}).nonstrict(),
  receipt: z.any().optional(),
  card: z.union(
    [z.object({
      achToken: z.string(),
      routingNumber: z.string()
    }), z.object({
      number: z.string(),
      expMonth: z.string(),
      expYear: z.string(),
      cvv: z.string().optional(),
    }), z.literal(false)]).nullable()
})

// Applies all .refine() validation rules to a Zod schema.
// Extracted so it can be reused for customValidations (which extend formBase and need the same refines).
export function applyFormRefines (schema: z.ZodTypeAny) {
  return schema
  .refine((form: any) => form.accountID || !['US', 'CA'].includes(form.country || '') || !!form.state, {
    path: ['state'],
    message: 'This field is required'
  })
  .refine(
    (form: any) =>
      !form.gateway?.toLowerCase().includes('cardknox') ||
      form.gateway?.toLowerCase().includes('sensepasscardknoxyachadcharity') ||
      form.currency == 'USD',
    {
      path: ['receipt'],
      message: 'When requesting a US receipt, currency must be USD'
    }
  )
  .refine(
    (form: any) =>
      !(form.gateway?.toLowerCase().includes('sensepasscardknoxyachadcharity')) ||
      form.currency == 'CAD',
    {
      path: ['receipt'],
      message: 'When requesting a CA receipt, the currency must be CAD'
    }
  )
  .refine((form: any) => (form.gateway == 'CardCom-ouisrael-donations' || form.gateway == 'CardCom-ouisrael-programs' || form.gateway == 'check' || form.gateway == 'wire') || expectsToken(form.gateway) || form.card !== null, {
    path: ['card'],
    message: 'This field is required'
  }).refine((form: any) => (form.gateway == 'CardCom-ouisrael-donations' || form.gateway == 'CardCom-ouisrael-programs' || form.gateway == 'check' || form.gateway == 'wire') || expectsToken(form.gateway) || form.card !== false, {
    path: ['card'],
    message: 'This card is invalid'
  }).refine((form: any) => ((form.gateway == 'CardCom-ouisrael-donations' || form.gateway == 'CardCom-ouisrael-programs') && form.toc) || form.gateway != 'CardCom-ouisrael-donations', {
    path: ['card'],
    message: 'Please accept the Terms and Conditions'
  })
  .refine((form: any) => !(form.gateway?.toLowerCase().includes('sensepassmonerisisrael')) || form.currency == 'CAD', {
    path: ['card'],
    message: 'When requesting a CA receipt, the currency must be CAD'
  })
  .refine((form: any) => !(form.gateway?.toLowerCase().includes('sensepasscardcom')) || form.currency == 'ILS' || form.currency == 'USD', {
    path: ['receipt'],
    message: 'When requesting an IL receipt, the currency must be ILS or USD'
  })
  .refine((form: any) => {
    if (form.accountID) {
      return true;
    } else {
      return form.firstName
    }
  }, {
    path: ['firstName'],
    message: 'This field is required'
  }).refine((form: any) => {
    if (form.accountID) {
      return true;
    } else {
      return form.lastName
    }
  }, {
    path: ['lastName'],
    message: 'This field is required'
  }).refine((form: any) => {
    if (form.accountID) {
      return true;
    } else {
      return form.email
    }
  }, {
    path: ['email'],
    message: 'This field is required'
  }).refine((form: any) => {
    if (form.accountID) {
      return true;
    } else {
      return form.phone
    }
  }, {
    path: ['phone'],
    message: 'This field is required'
  }).refine((form: any) => {
    if (form.accountID) {
      return true;
    } else {
      return form.street
    }
  }, {
    path: ['street'],
    message: 'This field is required'
  }).refine((form: any) => {
    if (form.accountID) {
      return true;
    } else {
      return form.city
    }
  }, {
    path: ['city'],
    message: 'This field is required'
  }).refine((form: any) => {
    if (form.accountID) {
      return true;
    } else {
      return form.zip
    }
  }, {
    path: ['zip'],
    message: 'This field is required'
  }).refine((form: any) => {
    if (form.accountID) {
      return true;
    } else {
      return form.country
    }
  }, {
    path: ['country'],
    message: 'This field is required'
  }).refine((form: any) => {
    if (form.accountID) {
      return true;
    } else {
      if (['US', 'CA'].includes(form.country || '')) {
        return form.state;
      } else {
        return true;
      }
    }
  }, {
    path: ['state'],
    message: 'This field is required'
  }).refine((form: any) => {
    // Require taxId when Israeli receipt is selected
    const isIsraeliReceipt =
      (form.gateway?.toLowerCase().includes('sensepasscardcom') &&
        !form.gateway?.toLowerCase().includes('sensepasscardcombit')) ||
      form.gateway?.toLowerCase().startsWith('cardcom-ouisrael');

    if (isIsraeliReceipt) {
      return typeof form.taxId === 'string' && form.taxId.trim().length > 0;
    }
    return true;
  }, {
    path: ['taxId'],
    message: 'Teudat Zehut is required for Israeli receipts'
  })
}

// formBase is a ZodObject (has .extend()), form is a ZodEffects (has .parse/.safeParse)
export const form = applyFormRefines(formBase);

// @ts-ignore - Complex Zod recursive type inference
type ValidForm = z.infer<typeof form>

type Context = {
   validationSchema?: typeof form,
   queuedForm?: Partial<Form>,
   queuedCustom?: Partial<Record<string, string>>,
   queuedApplications?: Array<{index: number,
     field: string,
     value: string}>,
   form: Form,
   validForm: ValidForm | null,
   touched: Partial<{ [K in keyof Form]: boolean }>
   validations: Partial<ErrorBag>,
   settings?: FormSettings,
   redirect: boolean,
   noCharge: boolean,
   testMode: boolean,
   gatewayMapping: any,
  lastTransactionId?: string | null,
  // Used to dedupe dynamic GA4 view_item pushes (amount + frequency signature)
  lastViewItemSignature?: string | null,
  // Snapshot of form data before clearForm, for purchase event
  formSnapshotForPurchase?: Form | null
}

type FormEvent =
  | { type: 'SET_VALUE'; field: keyof Form; value: any }
  | { type: 'SET_CUSTOM'; field: string; value: string }
  | { type: 'SET_APPLICATION'; index: number; field: keyof FormApplication; value: any }
  | { type: 'SET_TOUCHED'; field: keyof Form }
  | { type: 'SUBMIT' }
  | { type: 'CANCEL_SUBMIT' }

function emptyForm (): Form {
   return {
    applications: [],
    transactionFee: 2.5,
    dayOfMonth: null,
    dayOfWeek: null,
    periodMonths: null,
    periodWeeks: null,
    signup: false,
    title: '',
    firstName: null,
    lastName: null,
    accountID: null,
    taxId: null,
    street: null,
    street2: null,
    city: null,
    country: 'US',
    state: null,
    zip: null,
    amount: null,
    handleProcessing: false,
    currency: 'USD',
    email: null,
    phone: null,
    card: null,
    recipientName: null,
    recipientEmail: null,
    honoreeName: null,
    dedication: null,
    dedicationMessage: null,
    receipt: null,
    description: null,
    toc: false,
    gateway: null,
    custom: {},
    pledgeId: null
  }
}

export const machine = createMachine<Context, FormEvent, 'form'>({
  context: ({ input }) => ({
    gatewayMapping: null,
    testMode: input.testMode || false,
    form: input.form || {
      ...emptyForm(),
      applications: [{ amount: null, department: null, campaign: null, subCampaign: null }]
    },
    validForm: null,
    touched: {},
    validations: {},
    redirect: input.redirect || false,
    noCharge: input.noCharge || false,
    lastTransactionId: null,
    lastViewItemSignature: null,
    formSnapshotForPurchase: null,
    validationSchema: input.validationSchema,
    queuedApplications: input.queuedApplications || [],
    queuedForm: input.queuedForm || {},
    queuedCustom: input.queuedCustom || {}
  }),
  id: 'form',
  initial: 'loadingMapping',
  states: {
    loadingMapping: {
      on: {
        SET_VALUE: {
          actions: assign({
            queuedForm: ({ context, event }) => ({
              ...context.queuedForm,
              [event.field]: event.value
            })
          })
        },
        SET_CUSTOM: {
          actions: assign({
            queuedCustom: ({ context, event }) => ({
              ...context.queuedCustom,
              [event.field.replace('custom.', '')]: event.value
            })
          })
        },
        SET_APPLICATION: {
          actions: assign({
            queuedApplications: ({ context, event }) => {
              const queuedApps = context.queuedApplications || []

              queuedApps.push({
                index: event.index,
                field: event.field,
                //@ts-ignore
                value: event.field == 'amount' ? +event.value : event.value
              })

              return queuedApps
            }
          })
        }

      },
      invoke: {
        src: fromPromise(async () => {
          // Only fetch on client side to avoid SSR URL issues
          if (typeof window === 'undefined') {
            return { _source: {} } // Empty fallback for SSR
          }

          // Use static CloudFront URL for gateway mapping (no longer dependent on OpenSearch)
          const gatewayMappingUrl = 'https://department-gateway-mapping.oustatic.com/department-gateway-mapping.json'

          return fetch(gatewayMappingUrl)
            .then(async response => {
              if (!response.ok) {
                throw new Error(
                  `Failed to load gateway mapping from static URL (${response.status})`
                )
              }

              const data = await response.json()
              console.log('Successfully loaded gateway mapping from CloudFront static URL')
              // Wrap in _source for compatibility with frontend expectations
              return { _source: data }
            })
            .catch(err => {
              console.error('Gateway mapping fetch failed:', err)
              return { _source: {} }
            })
        }),
        onDone: {
          actions: [
            assign({
              gatewayMapping: ({ event }) => event.output._source,
              form: ({ context, event }) => {
                 const apps = Array.isArray(context.form.applications) ? context.form.applications : []
                 if (context.queuedApplications && context.queuedApplications.length > 0) {
                   for (const qa of context.queuedApplications) {
                     const application: any = apps[qa.index] || {
                       amount: null,
                       department: null,
                       campaign: null,
                       subCampaign: null
                     }
                     //@ts-ignore
                     application[qa.field] = qa.field == 'amount' ? +qa.value : qa.value
                     apps[qa.index] = application
                   }
                 }

                 if (!apps[0]?.department) {
                  return {
                     ...context.form,
                     applications: apps,
                     ...context.queuedForm,
                     custom: {
                       ...context.queuedCustom || {}
                     }
                   }
                 }
                 const gateways = event.output._source[apps[0].department]

                 // Fallback to a default gateway if department not found in mapping
                 const gateway = gateways
                   ? gateways[`${gateways.defaultReceipt}Receipt`]
                   : 'SensePassCardknox' // Default fallback

                 return {
                   ...context.form,
                   applications: apps,
                   ...context.queuedForm,
                   // Preserve an explicit URL/prop gateway override when the
                   // asynchronous department mapping finishes loading.
                   gateway: context.queuedForm?.gateway || gateway,
                   custom: {
                     ...context.queuedCustom || {}
                   }
                 }
               }
             }),
            // Fire GA4 view_item once per page load
            // Uses assign so it sees updated context from prior assign in same entry batch
            assign(({ context }: any) => {
              try {
                if (typeof window === 'undefined' || window.__viewItemSent) return {}
                window.__viewItemSent = true
                window.dataLayer = window.dataLayer || []
                const form: any = context.form || {}
                const apps: any[] = Array.isArray(form.applications) ? form.applications : []
                const primaryApp: any = apps[0] || {}
                const rawAmount = form.amount ?? primaryApp.amount
                const amountNum = parseFloat(String(rawAmount)) || 0
                const amount = amountNum.toFixed(2)
                const currency = form.currency || 'USD'
                const variant = form.dayOfMonth ? 'Monthly' : 'One-time'
                const dept = primaryApp.department || 'general'
                const item_name = (form.title || 'General Donation Form').trim().replace(/'/g, '\u2019')
                const affiliation = (typeof location !== 'undefined' ? location.pathname : '/') || '/'
                const item_id = 'donation-' + dept
                const signature = amount + '|' + variant + '|' + dept
                window.dataLayer.push({ ecommerce: null })
                window.dataLayer.push({
                  event: 'view_item',
                  ecommerce: {
                    currency,
                    value: amount,
                    items: [
                      {
                        item_id,
                        item_name,
                        affiliation,
                        item_category: 'Donation',
                        item_variant: variant,
                        quantity: 1,
                        price: amount
                      }
                    ]
                  }
                })
                return { lastViewItemSignature: signature }
              } catch (err) {
                console.warn('view_item push failed', err)
                return {}
              }
            })
          ],
          //@ts-ignore
          target: 'editing'
        },
        onError: {
          target: 'editing'
        }
      }
    },
    editing: {
      entry: ['scrollToError'],
      on: {
        SET_TOUCHED: {
          actions: ['setTouched', 'validate']
        },
        SET_CUSTOM: {
          actions: [assign({
            form: ({ context, event }) => ({
              ...context.form,
              custom: {
                ...context.form.custom,
                [event.field.replace('custom.', '')]: event.value
              }
            })
          }), 'validate']
        },
        SET_APPLICATION: {
          actions: [assign({
            form: ({ context, event }) => {
               const apps = Array.isArray(context.form.applications) ? [...context.form.applications] : [] as any
               const application = { ...(apps[event.index] || {
                 amount: null,
                 department: null,
                 campaign: null,
                 subCampaign: null
               }) }

               //@ts-ignore
               application[event.field] = event.value

               apps[event.index] = application

               let gateway = context.form.gateway

               if (event.field == 'department') {
                 const gateways = context.gatewayMapping?.[event.value]
                 if (gateways) {
                   gateway = gateways[`${context.form.currency?.slice(0,2)}Receipt`]
                 }
               }
               return {
                 ...context.form,
                 applications: apps,
                 gateway
               }
             }
           }), 'maybePushUpdatedViewItem', 'validate']
        },
        SET_VALUE: {
          actions: [
            'setValue',
            'maybePushUpdatedViewItem',
            'validate'
          ]
        },
        SUBMIT: {
          target: 'submitting'
        }
      }
    },
    submitting: {
      entry: ['validate', 'markAllAsTouched'],
      on: {
        CANCEL_SUBMIT: {
          target: 'editing'
        }
      },
      always: {
        guard: 'hasErrors',
        target: 'editing'
      },
      invoke: {
        src: 'submit',
        input: ({ context }) => context,
        onDone: [
          {
            guard: ({ event }) => event.output.success && event.output.message?.includes('https://'),
            actions: [
              ({ event }) => location.href = event.output.message
            ]
          },
          {
            guard: ({ event }) => event.output.success,
            actions: [
              assign({
                lastTransactionId: ({ event }: any) => {
                  // Only use transaction_id from API response
                  try {
                    const txnId = event?.output?.transaction_id || event?.output?.result?.transaction_id
                    return txnId ? String(txnId) : null
                  } catch {
                    return null
                  }
                },
                formSnapshotForPurchase: ({ context }) => JSON.parse(JSON.stringify(context.form))
              }),
              'pushPurchaseEvent'
            ],
            target: 'success'
          },
          {
            guard: ({ event }) => !event.output.success,
            target: 'declined'
          }
        ],
        onError: [
          {
            guard: ({ event }) => event.error?.message == 'CARD_NOT_VALID',
            actions: assign({
              validations: ({ context }) => ({
                ...context.validations,
                card: 'Card is not valid'
              })
            }),
            target: 'editing'
          },
          {
            target: 'error'
          }
        ]
      }
    },
    success: {
      entry: [
        'showSuccessMessage',
        'resetCard',
        'clearForm',
        'notifySucceeded',
        ({ context }) => {
          console.log('[FormMachine] Entered success state, redirect=', context.redirect)
        }
      ],
      always: [
        {
          guard: ({ context }) => {
            const shouldStayEditing = !context.redirect
            console.log('[FormMachine] success: redirect=', context.redirect, 'going to', shouldStayEditing ? 'editing' : 'submitted')
            return shouldStayEditing
          },
          target: 'editing'
        },
        {
          guard: ({ context }) => context.redirect,
          target: 'submitted'
        }]
    },
    declined: {
      entry: ['showDeclinedMessage'],
      exit: ['resetCard'],
      always: {
        target: 'editing'
      }
    },
    error: {
      entry: ['showDeclinedMessage'],
      exit: ['resetCard'],
      always: {
        target: 'editing'
      }
    },
    submitted: {
      // Stay on thank you page if redirect is enabled
      // Otherwise allow machine to continue accepting events for another submission
      entry: () => {
        console.log('[FormMachine] Entered submitted state - should show thank you page')
      },
      always: {
        guard: ({ context }) => {
          console.log('[FormMachine] submitted: checking if should go back to editing, redirect=', context.redirect)
          return !context.redirect
        },
        target: 'editing'
      }
    }

  },
},
  {
    actions: {
      scrollToError: ({ context }) => {
        if (Object.keys(context.validations).length > 0) {
          setTimeout(() => {
            // Try document first (works in Nuxt app), then check shadow DOM (web component)
            let focuser = document.querySelector('.error-label .focuser')
            if (!focuser) {
              const host = document.querySelector('donation-form')
              if (host?.shadowRoot) {
                focuser = host.shadowRoot.querySelector('.error-label .focuser')
              }
            }

            if (focuser) {
              //@ts-ignore
              focuser.scrollIntoView({ behavior: 'smooth', block: 'center' })
              //@ts-ignore
              focuser.focus()
            }
          }, 0)
        }
      },
      maybePushUpdatedViewItem: assign(({ context }: any) => {
        // Refactored: push a view_item whenever amount or frequency (variant) changes (after initial)
        try {
          if (typeof window === 'undefined') return
          //@ts-ignore
          if (!window.__viewItemSent) return // maintain GA4 sequence: require initial view_item first
          const form: any = context.form || {}
          const apps: any[] = Array.isArray(form.applications) ? form.applications : []
          const primaryApp: any = apps[0] || {}
          const rawAmount = form.amount ?? primaryApp.amount
          const amountNum = parseFloat(String(rawAmount))
          if (isNaN(amountNum) || amountNum <= 0) return
          const variant = form.dayOfMonth ? 'Monthly' : 'One-time'
          const dept = primaryApp.department || 'general'
          const signature = amountNum.toFixed(2) + '|' + variant + '|' + dept
          if (context.lastViewItemSignature === signature) return
          //@ts-ignore
          window.dataLayer = window.dataLayer || []
          const toMoneyStr = (v: any) => {
            const n = parseFloat(String(v || 0))
            return isNaN(n) ? '0.00' : n.toFixed(2)
          }
          const sanitize = (v: any) => {
            if (v == null) return null
            if (typeof v === 'string') return v.trim().replace(/'/g, '’') || null
            return v
          }
          const amount = toMoneyStr(amountNum)
          const currency = form.currency || 'USD'
          const item_name = sanitize(form.title) || 'General Donation Form'
          const affiliation = (typeof location !== 'undefined' ? location.pathname : '/') || '/'
          const item_id = 'donation-' + dept
          window.dataLayer.push({ ecommerce: null })
          window.dataLayer.push({
            event: 'view_item',
            ecommerce: {
              currency,
              value: amount,
              items: [
                {
                  item_id,
                  item_name,
                  affiliation,
                  item_category: 'Donation',
                  item_variant: variant,
                  quantity: 1,
                  price: amount
                }
              ]
            }
          })
          return { lastViewItemSignature: signature }
        } catch (err) {
          console.warn('dynamic view_item push failed', err)
          return {}
        }
      }),
      showSuccessMessage: ({ context }) => {
        if (context.noCharge) return
        // @ts-ignore
        (context.form as any).gateway == 'check' ? alert('Your pledge has been recorded!') : alert('Charge was successful')
        // alert is synchronous; purchase event will fire after dismissal via pushPurchaseEvent
      },
      pushPurchaseEvent: ({ context }) => {
        // Use the snapshot captured before any state transitions
        const formSnapshot = context.formSnapshotForPurchase || context.form
        try {
          if (typeof window === 'undefined') return
          //@ts-ignore
          window.dataLayer = window.dataLayer || []
          //@ts-ignore
          window.__sentPurchaseIds = window.__sentPurchaseIds || new Set<string>()
          const form: any = formSnapshot || {}
          const apps: any[] = Array.isArray(form.applications) ? form.applications : []
          console.log('Purchase debug - captured form data:', {
            formAmount: form.amount,
            apps: apps.map(a => ({ amount: a.amount, department: a.department })),
            appsLength: apps.length
          })
          // Require API-provided transaction_id; if missing, skip pushing purchase
          const transactionId = context.lastTransactionId
          if (!transactionId) {
            console.warn('purchase push skipped: missing API transaction_id')
            return
          }
          // Build a lightweight fingerprint to help prevent accidental duplicate pushes on rapid resubmits (amounts+dept+freq)
          const fingerprint = (() => {
            try {
              const freq = form.dayOfMonth ? 'M' : 'O'
              const parts = apps.map(a => [a?.department||'', a?.campaign||'', a?.subCampaign||'', a?.amount||'0'].join(':')).join('|')
              return [parts, freq].join('#')
            } catch { return 'fp' }
          })()
          try {
            const key = 'sentTxnIds'
            const raw = sessionStorage.getItem(key)
            const sent: string[] = raw ? JSON.parse(raw) : []
            if (transactionId && sent.includes(transactionId)) return
            if (transactionId && (window as any).__sentPurchaseIds.has(transactionId)) return
            if (transactionId) (window as any).__sentPurchaseIds.add(transactionId)
            if (transactionId) sent.push(transactionId)
            sessionStorage.setItem(key, JSON.stringify(sent))
            const lkey = 'lastPurchaseId'
            if (localStorage.getItem(lkey) === transactionId) return
            if (transactionId) localStorage.setItem(lkey, transactionId)
            // Fingerprint dedupe (5 min TTL)
            const fpKey = 'purchaseFingerprints'
            const fpRaw = localStorage.getItem(fpKey)
            const fp: Record<string, number> = fpRaw ? JSON.parse(fpRaw) : {}
            const now = Date.now()
            // purge old
            Object.keys(fp).forEach(k => { if (now - fp[k] > 5*60*1000) delete fp[k] })
            if (fingerprint in fp) return
            fp[fingerprint] = now
            localStorage.setItem(fpKey, JSON.stringify(fp))
          } catch { }
          const toMoneyStr = (v: any) => {
            const n = parseFloat(String(v || 0))
            if (isNaN(n)) return '0.00'
            return n.toFixed(2) // Always 2 decimals; string value
          }
          const sanitize = (v: any): any => {
            if (v === undefined || v === null) return null
            if (typeof v === 'string') {
              const trimmed = v.trim()
              if (!trimmed) return null
              return trimmed.replace(/'/g, '’')
            }
            return v
          }
          const frequency = form.dayOfMonth ? 'Monthly' : 'One-time'
          const pagePath = typeof location !== 'undefined' ? location.pathname || '/' : '/'
          const formName = sanitize(form.title) || 'General Donation Form'
          const isDedicated = !!(form.dedication || form.dedicationMessage || form.honoreeName || form.recipientName || form.recipientEmail)
          const dedicationCategory = isDedicated ? 'Dedication: In honor of' : null
          const designation = sanitize((apps[0] && (apps[0].campaign || apps[0].department || apps[0].subCampaign)) || 'Needed Most')
          const optIns: string[] = []
          const optOuts: string[] = []
          if (form.email) optIns.push('Email')
            if (form.phone) optIns.push('SMS')
            if (form.signup) optIns.push('Monthly')
          try {
            const custom = form.custom || {}
            Object.entries(custom).forEach(([k, v]) => {
              if (v === true) optIns.push(k)
              else if (v === false) optOuts.push(k)
            })
          } catch { }
          const optInCategory = optIns.length ? `Opt-in: ${optIns.join(', ')}` : null
          const optOutCategory = optOuts.length ? `Opt-out: ${optOuts.join(', ')}` : null
          // Build items with string price (internal spec) with fallback to top-level form.amount when single app amount missing
          const items = apps.map((application: any, idx: number) => {
            // Enhanced fallback: use form.amount if application amount is missing, null, empty, or 0
            const appAmount = parseFloat(String(application.amount || 0)) || 0
            const formAmount = parseFloat(String(form.amount || 0)) || 0
            const rawAmt = (apps.length === 1 && appAmount === 0 && formAmount > 0) ? formAmount : appAmount
            const priceStr = toMoneyStr(rawAmt)
            console.log('Purchase item debug:', { idx, appAmount, formAmount, rawAmt, priceStr, application })
            return {
              item_id: (application.department ? 'don-' + application.department : 'don-' + (idx+1)),
              item_name: formName,
              affiliation: pagePath,
              item_category: 'Donation',
              item_category2: dedicationCategory,
              item_category3: designation,
              item_category4: optInCategory,
              item_category5: optOutCategory,
              item_variant: frequency,
              price: priceStr,
              quantity: 1
            }
          })
          // Calculate total value as sum of item prices * quantity (fallback to form.amount if empty)
          const sumItems = items.reduce((acc, it) => acc + (parseFloat(it.price) * (it.quantity || 1)), 0)
          const totalValue = items.length ? sumItems : (parseFloat(String(form.amount || 0)) || 0)
          const tax = form.handleProcessing && totalValue ? +( (totalValue * (form.transactionFee || 0) / 100).toFixed(2) ) : 0
          // Convert to strings per internal spec
          const totalValueStr = toMoneyStr(totalValue)
          const taxStr = toMoneyStr(tax)
          window.dataLayer.push({ ecommerce: null })
          const eventName = context.testMode ? 'purchase_test' : 'purchase'
          const payload: any = {
            event: eventName,
            ecommerce: {
              transaction_id: transactionId,
              affiliation: pagePath,
              value: totalValueStr,
              currency: form.currency || 'USD',
              tax: taxStr,
              items: items
            }
          }
          console.log(payload)
          window.dataLayer.push(payload)
        } catch (err) {
            console.warn('purchase push failed', err)
        }
      },
      showDeclinedMessage: ({ event }) => {
        // In XState v5, event data is in event.output (for onDone) or event.error (for onError)
        //@ts-ignore
        const message = event.output || event.error
        if (!message) {
          console.error('showDeclinedMessage called with no message data', event)
          console.error('Full event object:', JSON.stringify(event, null, 2))
          alert(`A technical error occurred. Check console for details.`)
          return
        }
        if (message.message === 'CAPTCHA_NOT_AVAILABLE') {
          alert('Could not complete the security check. Please refresh the page and try again.')
          return
        }
        if ('success' in message)
          alert(`Card Declined: ${message.message}`)
        else {
          if (message.message && message.message.includes('Tokenization')) {
            if (message.message.includes('Load failed') || message.message.includes('Failed to fetch')) {
              alert('Could not connect to payment system. Please check that your internet connection is working, and that you have no internet filters blocking payment.ou.org')
              return
            }
            alert(message.message.replace('Tokenization ', ''))

            return
          }
          alert('A technical error occurred while processing the card. Contact support')
          console.error(message.message || message)
          Sentry.captureException(new Error(message.message || JSON.stringify(message)));
        }
      },
      clearForm: assign(({ context }: Context) => {
        const prevFirst = (context.form as any).applications?.[0] || {}
        const base = emptyForm()
        return {
          ...context,
          form: {
            ...base,
            applications: [
              {
                amount: null,
                department: prevFirst.department || null,
                campaign: prevFirst.campaign || null,
                subCampaign: prevFirst.subCampaign || null
              }
            ],
            // carry over currency & gateway if needed
            currency: (context.form as any).currency || base.currency,
            gateway: (context.form as any).gateway || base.gateway,
            transactionFee: (context.form as any).transactionFee ?? base.transactionFee,
            title: '',
            signup: false,
            toc: false,
            custom: {}
          } as Form,
          validForm: null,
          touched: {}
        }
      }),
      markAllAsTouched: assign(({ context }: Context) => {
        // Walk the Zod validation schema to find all possible field names,
        // including nested fields like custom.SpecialEdition → "custom,SpecialEdition".
        // This ensures fields that haven't been populated yet still get marked as touched.
        function getFields (shape: object, prefix = ''): string[] {
          return Object.entries(shape).flatMap(x => x[1].shape == undefined ? prefix + x[0] : getFields(x[1].shape, x[0] + ','))
        }

        const touched: Record<string, boolean> = {}

        // Schema-based approach: mark all fields defined in the validation schema.
        // Unwrap ZodEffects (.refine() chains) to find the inner ZodObject's shape.
        let schema = context.validationSchema as any
        while (schema && !('shape' in schema) && schema._def?.schema) {
          schema = schema._def.schema
        }
        const shape = schema && 'shape' in schema ? (schema as any).shape : null
        if (shape) {
          for (const field of getFields(shape)) {
            touched[field] = true
          }
        }

        // Also mark fields from the live form object (catches dynamically set custom fields
        // that may not be in the schema, e.g. custom fields set via query params)
        for (const key of Object.keys(context.form)) {
          touched[key] = true
          const value = (context.form as any)[key]
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            for (const subKey of Object.keys(value)) {
              touched[`${key},${subKey}`] = true
            }
          }
        }

        return { touched }
      }),
      setTouched: assign(({ context, event }: Context) => ({
        touched: {
          ...context.touched,
          [event.field]: true
        }
      })),
      setValue: assign(({ context, event }: Context) => {
        const apps = Array.isArray(context.form.applications) ? context.form.applications as any[] : (context.form.applications = [] as any)
        if (event.field == 'amount' && apps.length == 1 && event.value) {
          // @ts-ignore
          apps[0].amount = +event.value
        }
        let gateway = context.form.gateway
        if (event.field == 'currency') {
          const department = apps[0]?.department
          if (department && context.gatewayMapping?.[department]) {
            gateway = context.gatewayMapping[department][`${event.value?.slice(0,2)}Receipt`] || gateway
          }
        } else if (event.field == 'gateway') {
          gateway = event.value
        }
        return {
          form: {
            ...context.form,
            [event.field]: event.value,
            gateway
          } as any
        }
      }),
      validate: assign(({ context }: Context) => {
        const parseResult = context.validationSchema!.safeParse(context.form)

        let validForm = null
        let validations = {}

        if (parseResult.success) {
          validForm = parseResult.data
        } else {
          console.log(parseResult.error)
          validations = Object.fromEntries(parseResult.error.errors.map(x => [x.path, x.message])) as any

          // When customValidations extends formBase with strict fields (e.g. mediaRelease: z.literal(true)),
          // the ZodObject parse fails on those fields, which prevents the .refine() chain from running.
          // This means required field errors (firstName, email, etc.) never appear.
          // Fix: also parse with the base form schema to collect required field errors and merge them.
          if (context.validationSchema !== form) {
            const baseResult = form.safeParse(context.form)
            if (!baseResult.success) {
              const baseErrors = Object.fromEntries(baseResult.error.errors.map(x => [x.path, x.message])) as any
              validations = { ...baseErrors, ...validations }
            }
          }
        }

        return {
          validForm,
          validations
        }
      })
    },
    guards: {
      hasErrors: ({ context }: Context) => Object.keys(context.validations).length > 0
     }
   })

// NOTE: GA4 pushPurchaseEvent defined in machine actions above (dedup + transaction id)
